"use client";

import React, { useState, useEffect, useCallback } from "react";
import Link from "next/link";
import Image from "next/image";
import { useRouter, usePathname } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useBookingModal } from "@/contexts/BookingModalContext";
import i18n, { LANG_STORAGE_KEY, LANG_COOKIE_NAME } from "@/lib/i18n";
import { Menu, X, ChevronDown } from "lucide-react";

import { routes } from "@/lib/routes";

/** English app paths (same pages; may appear before redirect). */
const servicesLinks = [
  { href: routes.services.pitches, labelKey: "header.pitches", enPrefix: "/services/pitches" },
  { href: routes.services.tournaments, labelKey: "header.tournaments", enPrefix: "/services/tournaments" },
  { href: routes.services.birthdays, labelKey: "header.birthdays", enPrefix: "/services/birthdays" },
  { href: routes.services.otherSports, labelKey: "header.otherSports", enPrefix: "/services/other-sports" },
  { href: routes.services.howToGetThere, labelKey: "header.howToGetThere", enPrefix: "/services/how-to-get-there" },
] as const;

/** Normalize pathname for comparison (no trailing slash except root). */
function trimPath(path: string) {
  if (path.length > 1 && path.endsWith("/")) return path.slice(0, -1);
  return path;
}

function isHomeActive(pathname: string) {
  const p = trimPath(pathname);
  return p === "" || p === "/";
}

/** Exact match, or any of alternate legacy paths (before redirect). */
function isExactRoute(pathname: string, canonical: string, alternates: string[] = []) {
  const p = trimPath(pathname);
  if (p === trimPath(canonical)) return true;
  return alternates.some((a) => p === trimPath(a));
}

/** Services section or English /services/* URLs. */
function isServicesSectionActive(pathname: string) {
  const p = trimPath(pathname);
  return p.startsWith("/servicios") || p.startsWith("/services");
}

/** Nested service pages (e.g. pitches + pitch variants); supports EN URLs. */
function isServiceLinkActive(pathname: string, href: string, enPrefix?: string) {
  const p = trimPath(pathname);
  const h = trimPath(href);
  if (p === h || p.startsWith(`${h}/`)) return true;
  if (enPrefix) {
    const e = trimPath(enPrefix);
    return p === e || p.startsWith(`${e}/`);
  }
  return false;
}

const desktopNavLinkBase =
  "flex min-h-[44px] min-w-[44px] items-center text-sm font-medium transition";
const desktopNavLinkInactive = `${desktopNavLinkBase} text-slate-700 hover:text-primary-strong`;
const desktopNavLinkActive = `${desktopNavLinkBase} font-semibold text-primary-strong`;

const mobileNavLinkBase =
  "flex min-h-11 items-center rounded-xl px-4 py-3 text-base font-medium transition";
const mobileNavLinkInactive = `${mobileNavLinkBase} text-slate-700 hover:bg-slate-50 hover:text-primary-strong active:bg-slate-50`;
const mobileNavLinkActive = `${mobileNavLinkBase} font-semibold text-primary-strong`;

function isMainNavLinkActive(href: string, pathname: string) {
  if (href === routes.home) return isHomeActive(pathname);
  if (href === routes.about) return isExactRoute(pathname, routes.about, ["/about"]);
  if (href === routes.pricing) return isExactRoute(pathname, routes.pricing, ["/pricing"]);
  return false;
}

export default function Header() {
  const { t } = useTranslation();
  const router = useRouter();
  const pathname = usePathname();
  const { openModal } = useBookingModal();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  // Programmatic navigation to prevent click hijacking (e.g. crypto redirect attacks)
  const handleLogoClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setMobileMenuOpen(false);
      if (pathname !== routes.home) {
        router.push(routes.home);
      }
    },
    [pathname, router]
  );

  const mainNavLinks = [
    { href: routes.home, label: t("header.home") },
    { href: routes.about, label: t("header.about") },
    { href: routes.pricing, label: t("header.pricing") },
  ];

  const contactNavActive = isExactRoute(pathname, routes.contact, ["/contact"]);
  const servicesSectionActive = isServicesSectionActive(pathname);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  const handleLanguageChange = (lang: string) => {
    void i18n.changeLanguage(lang);
    if (typeof window !== "undefined") {
      localStorage.setItem(LANG_STORAGE_KEY, lang);
      document.cookie = `${LANG_COOKIE_NAME}=${lang}; path=/; max-age=31536000; SameSite=Lax`;
      router.refresh();
    }
  };

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  return (
    <header className="sticky top-0 z-50 border-b border-border-soft bg-white/95 backdrop-blur supports-[backdrop-filter:blur(0)]:bg-white/80 safe-area-inset-top">
      <div className="mx-auto flex h-14 min-h-[3.5rem] w-full max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
        <Link
          href={routes.home}
          className="inline-flex shrink-0 items-center gap-2"
          onClick={handleLogoClick}
          aria-label={t("header.home")}
        >
          <Image
            src="/logo.png"
            alt={t("common.brandName")}
            width={40}
            height={40}
            priority
            className="h-8 w-8 sm:h-9 sm:w-9 object-contain"
          />
          <span className="text-base font-bold tracking-tight text-primary-strong sm:text-lg">
            {t("common.brandName")}
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-6 md:flex md:gap-8" aria-label="Main">
          {mainNavLinks.map(({ href, label }) => {
            const active = isMainNavLinkActive(href, pathname);
            return (
              <Link
                key={href}
                href={href}
                className={active ? desktopNavLinkActive : desktopNavLinkInactive}
                aria-current={active ? "page" : undefined}
              >
                {label}
              </Link>
            );
          })}
          <div className="relative">
            <button
              type="button"
              onClick={() => setServicesOpen(!servicesOpen)}
              onBlur={() => setTimeout(() => setServicesOpen(false), 150)}
              className={
                servicesSectionActive ? `${desktopNavLinkActive} gap-1` : `${desktopNavLinkInactive} gap-1`
              }
              aria-expanded={servicesOpen}
              aria-haspopup="true"
              aria-current={servicesSectionActive ? "page" : undefined}
            >
              {t("header.services")}
              <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            {servicesOpen && (
              <div className="absolute left-0 top-full z-50 mt-1 min-w-[180px] rounded-lg border border-slate-200 bg-white py-2 shadow-lg">
                {servicesLinks.map(({ href, labelKey, enPrefix }) => {
                  const active = isServiceLinkActive(pathname, href, enPrefix);
                  return (
                    <Link
                      key={href}
                      href={href}
                      className={
                        active
                          ? "block px-4 py-2 text-sm font-semibold text-primary-strong"
                          : "block px-4 py-2 text-sm text-slate-700 transition hover:bg-slate-50 hover:text-primary-strong"
                      }
                      onClick={() => setServicesOpen(false)}
                      aria-current={active ? "page" : undefined}
                    >
                      {t(labelKey)}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
          <Link
            href={routes.contact}
            className={contactNavActive ? desktopNavLinkActive : desktopNavLinkInactive}
            aria-current={contactNavActive ? "page" : undefined}
          >
            {t("header.contact")}
          </Link>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <select
            value={i18n.language?.startsWith("en") ? "en" : "es-AR"}
            onChange={(e) => handleLanguageChange(e.target.value)}
            className="min-h-[44px] min-w-[44px] rounded-lg border border-slate-200 bg-white px-2.5 py-2.5 text-sm font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-primary/30 cursor-pointer sm:px-3"
            aria-label="Language"
          >
            <option value="es-AR">ES</option>
            <option value="en">EN</option>
          </select>

          <button
            type="button"
            onClick={openModal}
            className="hidden min-h-[44px] min-w-[44px] rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-primary-strong sm:inline-flex sm:items-center sm:gap-2 sm:px-5"
          >
            {t("header.bookSlot")}
          </button>

          {/* Mobile menu toggle */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="flex h-11 w-11 min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-slate-700 transition hover:bg-slate-100 md:hidden"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile nav overlay */}
      <div
        className={`fixed inset-0 top-14 z-40 bg-slate-900/50 backdrop-blur-sm transition-opacity md:hidden ${
          mobileMenuOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={closeMobileMenu}
        aria-hidden
      />

      {/* Mobile nav drawer */}
      <nav
        className={`fixed right-0 top-14 z-50 flex h-[calc(100dvh-3.5rem)] w-full max-w-sm flex-col gap-1 overflow-y-auto border-l border-border-soft bg-white p-4 shadow-xl transition-transform duration-200 ease-out md:hidden ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        aria-label="Mobile navigation"
      >
        {mainNavLinks.map(({ href, label }) => {
          const active = isMainNavLinkActive(href, pathname);
          return (
            <Link
              key={href}
              href={href}
              onClick={closeMobileMenu}
              className={active ? mobileNavLinkActive : mobileNavLinkInactive}
              aria-current={active ? "page" : undefined}
            >
              {label}
            </Link>
          );
        })}
        <Link
          href={routes.contact}
          onClick={closeMobileMenu}
          className={contactNavActive ? mobileNavLinkActive : mobileNavLinkInactive}
          aria-current={contactNavActive ? "page" : undefined}
        >
          {t("header.contact")}
        </Link>
        <div className="border-t border-slate-200 pt-3">
          <p
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider ${
              servicesSectionActive ? "text-primary-strong" : "text-slate-600"
            }`}
          >
            {t("header.services")}
          </p>
          {servicesLinks.map(({ href, labelKey, enPrefix }) => {
            const active = isServiceLinkActive(pathname, href, enPrefix);
            return (
              <Link
                key={href}
                href={href}
                onClick={closeMobileMenu}
                className={active ? mobileNavLinkActive : mobileNavLinkInactive}
                aria-current={active ? "page" : undefined}
              >
                {t(labelKey)}
              </Link>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() => {
            closeMobileMenu();
            openModal();
          }}
          className="mt-4 flex min-h-[2.75rem] w-full items-center justify-center rounded-xl bg-primary px-4 py-3 text-base font-semibold text-white shadow-md transition hover:bg-primary-strong active:bg-primary-strong/90"
        >
          {t("header.bookSlot")}
        </button>
      </nav>
    </header>
  );
}
